import { useSlideshowContext } from '@fukku/slideshow/hooks/useSlideshowContext/useSlideshowContext'
import { useSlideshowTouch } from '@fukku/slideshow/hooks/useSlideshowTouch/useSlideshowTouch'
import styles from '@fukku/slideshow/Slideshow.module.scss'
import { SlideshowAnimation } from '@fukku/slideshow/types/Slideshow'
import {
	type CSSProperties,
	cloneElement,
	type JSX,
	useCallback,
	useEffect,
	useMemo,
} from 'react'
import { cx } from 'utils/cx'
import { useStore } from 'zustand'

type SlideshowItemsProps = {
	readonly initialAnimation: SlideshowAnimation | undefined
}

export const SlideshowItems = ({ initialAnimation }: SlideshowItemsProps) => {
	const {
		slideshowRef,
		items,
		nItems,
		defaultActiveItemIndex,
		updateStyles,
		onAnimationEnd,
		activeItemIndexStore,
		bulletIndexStore,
		setPosition,
	} = useSlideshowContext()

	const bulletIndex = useStore(bulletIndexStore, (state) => state.index)

	const activeItemIndex = useStore(activeItemIndexStore, (state) => state.index)

	const { onTouchEnd, onTouchMove, onTouchStart } = useSlideshowTouch()

	const shouldAnimate = !!initialAnimation && nItems > 1

	const animationClasses = useMemo(() => {
		if (!shouldAnimate) {
			return []
		}

		const classes = [styles.animate]
		if (initialAnimation === SlideshowAnimation.Long) {
			classes.push(styles.longAnimation)
		}
		if (initialAnimation === SlideshowAnimation.Short) {
			classes.push(styles.shortAnimation)
		}
		return classes
	}, [shouldAnimate, initialAnimation])

	const onTransitionEnd = useCallback(() => {
		updateStyles({ transitionDuration: 0 })
	}, [updateStyles])

	useEffect(() => {
		// Ensure active index is within bounds when nItems changes, if not go to the first item
		if (activeItemIndex >= nItems) {
			setPosition(0)
		}
	}, [activeItemIndex, setPosition, nItems])

	return (
		<div
			onTransitionEnd={onTransitionEnd}
			onTouchStart={onTouchStart}
			onTouchMove={onTouchMove}
			onTouchEnd={onTouchEnd}
			onAnimationEnd={onAnimationEnd}
			className={cx(styles.slideshow, ...animationClasses)}
			ref={slideshowRef}
			data-testid='slideshow'
			style={
				{
					'--slideshow-offset': '0px',
					'--slideshow-active-item-index': `${defaultActiveItemIndex}`,
					'--slideshow-transition-duration': '0ms',
				} as CSSProperties
			}
		>
			{items.map((child: JSX.Element, idx) => (
				// biome-ignore lint/a11y/useSemanticElements: Prefer <div> with group role and aria-label over <fieldset> as it provides a more accurate representation of the content.
				<div
					role='group'
					className={styles.slide}
					aria-label={`${idx + 1} / ${items.length}`}
					key={child.props['data-testid'] ?? `slideshow-item-${idx}`}
					data-testid={`slideshow-item-${idx}`}
					aria-roledescription='slide'
					aria-hidden={bulletIndex !== idx}
				>
					{cloneElement(child, {
						...child.props,
						...(bulletIndex !== idx && { tabIndex: '-1' }),
					})}
				</div>
			))}
		</div>
	)
}
